'use client';

import React, { useState } from 'react';
import { Copy, CheckCheck, MessageCircle, Link2, ExternalLink, BarChart2, Shield } from 'lucide-react';
import type { EventConfig } from './EventSetupClient';

interface ShareSectionProps {
  shareUrl: string;
  organizerUrl?: string;
  config: EventConfig;
}

export default function ShareSection({ shareUrl, organizerUrl, config }: ShareSectionProps) {
  const [copied, setCopied] = useState(false);
  const [copiedOrganizer, setCopiedOrganizer] = useState(false);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select text
    }
  };

  const handleCopyOrganizer = async () => {
    if (!organizerUrl) return;
    try {
      await navigator.clipboard.writeText(organizerUrl);
      setCopiedOrganizer(true);
      setTimeout(() => setCopiedOrganizer(false), 2000);
    } catch {
      // fallback
    }
  };

  const buildWhatsAppMessage = () => {
    const p1 = config.parentName1 || 'Mamá';
    const p2 = config.parentName2 || 'Papá';
    const title = config.eventTitle || 'Revelación de Género';
    const msg = `🎉 *${title}*\n\n${p1} & ${p2} te invitan a adivinar:\n¿Es niño 💙 o niña 🩷?\n\n👉 Vota aquí: ${shareUrl}\n\n¡Tu opinión cuenta! 🥰`;
    return encodeURIComponent(msg);
  };

  const whatsappUrl = `https://wa.me/?text=${buildWhatsAppMessage()}`;

  return (
    <div className="space-y-4">
      {/* Guest share link */}
      <div className="bg-card rounded-2xl border border-border card-shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-boy-gradient flex items-center justify-center">
            <Link2 size={14} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-700 text-foreground">Compartir Enlace</h3>
            <p className="text-xs text-muted-foreground">Envía a familiares y amigos</p>
          </div>
        </div>

        {/* URL box */}
        <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-xl px-3 py-2.5 mb-4">
          <Link2 size={14} className="text-muted-foreground shrink-0" />
          <span className="flex-1 text-xs text-foreground font-500 truncate font-tabular">
            {shareUrl || 'Guarda el evento primero para generar el enlace'}
          </span>
          <button
            onClick={handleCopy}
            disabled={!shareUrl}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-card border border-border text-xs font-600 text-foreground hover:bg-muted transition-all active:scale-95 disabled:opacity-40"
          >
            {copied ? (
              <>
                <CheckCheck size={12} className="text-green-500" />
                Copiado
              </>
            ) : (
              <>
                <Copy size={12} />
                Copiar
              </>
            )}
          </button>
        </div>

        {/* WhatsApp button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-700 text-sm text-white transition-all duration-200 active:scale-95 ${
            shareUrl
              ? 'bg-[#25D366] hover:bg-[#20bd5a] card-shadow'
              : 'bg-gray-300 pointer-events-none'
          }`}
        >
          <MessageCircle size={18} />
          Compartir por WhatsApp
          <ExternalLink size={14} className="opacity-70" />
        </a>

        {/* Other share options */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border text-xs font-600 text-foreground hover:bg-muted transition-all active:scale-95"
          >
            <Copy size={13} />
            Copiar enlace
          </button>
          <a
            href={`sms:?body=${encodeURIComponent(`¿Niño o niña? Vota aquí: ${shareUrl}`)}`}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border text-xs font-600 text-foreground hover:bg-muted transition-all"
          >
            <MessageCircle size={13} />
            Enviar SMS
          </a>
        </div>

        {/* Instructions */}
        <div className="mt-4 p-3 rounded-xl bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">
            💡 <span className="font-600 text-foreground">Tip:</span> Puedes compartir el enlace antes de la celebración y luego revelar los resultados juntos en la fiesta con la pantalla de revelación.
          </p>
        </div>

        {/* Public results link */}
        {shareUrl && (
          <a
            href="/public-results"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-xs font-600 text-foreground hover:bg-muted transition-all active:scale-95"
          >
            <BarChart2 size={13} />
            Ver resultados públicos
            <ExternalLink size={11} className="opacity-60" />
          </a>
        )}
      </div>

      {/* Organizer private link */}
      {organizerUrl && (
        <div className="bg-card rounded-2xl border border-primary/20 card-shadow p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
              <Shield size={14} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-700 text-foreground">Enlace privado del organizador</h3>
              <p className="text-xs text-muted-foreground">Solo para ti — no compartas este enlace</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2.5 mb-3">
            <Shield size={13} className="text-violet-400 shrink-0" />
            <span className="flex-1 text-xs text-foreground font-500 truncate font-tabular">
              {organizerUrl}
            </span>
            <button
              onClick={handleCopyOrganizer}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-card border border-violet-200 text-xs font-600 text-foreground hover:bg-violet-50 transition-all active:scale-95"
            >
              {copiedOrganizer ? (
                <>
                  <CheckCheck size={12} className="text-green-500" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy size={12} />
                  Copiar
                </>
              )}
            </button>
          </div>

          <a
            href={organizerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-700 hover:opacity-90 transition-all active:scale-95"
          >
            <Shield size={13} />
            Abrir panel del organizador
            <ExternalLink size={11} className="opacity-70" />
          </a>

          <p className="text-xs text-muted-foreground mt-2.5 text-center">
            🔒 Este enlace te da acceso exclusivo para ver y gestionar todos los votos
          </p>
        </div>
      )}
    </div>
  );
}